import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Loader2, Plus, Trash2, Save, Radar, Package, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { labelize } from '@/lib/sentinelConstants';
import ProcessHelp from '@/components/ProcessHelp';

const OPERATORS = [
  { v: 'gt', l: 'above (>)' }, { v: 'gte', l: 'at or above (≥)' },
  { v: 'lt', l: 'below (<)' }, { v: 'lte', l: 'at or below (≤)' },
];
const DIRECTIONS = [{ v: 'any', l: 'either way' }, { v: 'up', l: 'up' }, { v: 'down', l: 'down' }];

// Per-detection-type parameter editors (field key → {label, kind, options}).
const FIELD_SPEC = {
  static_threshold: [{ k: 'operator', l: 'When value is', kind: 'op' }, { k: 'value', l: 'Threshold', kind: 'num' }],
  dynamic_threshold: [{ k: 'baseline_window', l: 'Baseline window (periods)', kind: 'num' }, { k: 'band_sigma', l: 'Band width (σ)', kind: 'num' }],
  rate_of_change: [{ k: 'max_pct', l: 'Max % change / period', kind: 'num' }, { k: 'direction', l: 'Direction', kind: 'dir' }],
  sustained_trend: [{ k: 'min_periods', l: 'Consecutive periods', kind: 'num' }, { k: 'direction', l: 'Direction', kind: 'dir' }],
  seasonal_anomaly: [{ k: 'z', l: 'Deviation (σ)', kind: 'num' }, { k: 'season', l: 'Season length (0=auto)', kind: 'num' }],
  forecast_threshold: [{ k: 'operator', l: 'When projected value is', kind: 'op' }, { k: 'value', l: 'Threshold', kind: 'num' }, { k: 'horizon', l: 'Within N periods', kind: 'num' }],
  forecast_variance: [{ k: 'max_pct', l: 'Max % vs last actual', kind: 'num' }],
  data_quality: [{ k: 'stale_days', l: 'Stale after (days)', kind: 'num' }],
};

function DetectionEditor({ det, meta, onChange, onRemove }) {
  const spec = FIELD_SPEC[det.type] || [];
  const setParam = (k, v) => onChange({ ...det, params: { ...det.params, [k]: v } });
  return (
    <div className="rounded-lg border border-border/60 p-3 space-y-2" data-testid={`detection-${det.type}`}>
      <div className="flex items-center justify-between">
        <div>
          <Badge variant="outline">{labelize(det.type)}</Badge>
          <span className="text-xs text-muted-foreground ml-2">{meta?.desc}</span>
        </div>
        <Button size="sm" variant="ghost" className="text-rose-500 h-7 px-2" onClick={onRemove} data-testid={`detection-remove-${det.type}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {spec.map((f) => (
          <div key={f.k}>
            <Label className="text-[11px] text-muted-foreground">{f.l}</Label>
            {f.kind === 'op' ? (
              <Select value={String(det.params[f.k] ?? 'gt')} onValueChange={(v) => setParam(f.k, v)}>
                <SelectTrigger className="h-8" data-testid={`param-${det.type}-${f.k}`}><SelectValue /></SelectTrigger>
                <SelectContent>{OPERATORS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
              </Select>
            ) : f.kind === 'dir' ? (
              <Select value={String(det.params[f.k] ?? 'any')} onValueChange={(v) => setParam(f.k, v)}>
                <SelectTrigger className="h-8" data-testid={`param-${det.type}-${f.k}`}><SelectValue /></SelectTrigger>
                <SelectContent>{DIRECTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input type="number" className="h-8" value={det.params[f.k] ?? ''} data-testid={`param-${det.type}-${f.k}`}
                onChange={(e) => setParam(f.k, e.target.value === '' ? '' : Number(e.target.value))} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SentinelBuilderPage() {
  const { sentinelId } = useParams();
  const isEdit = !!sentinelId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const packId = searchParams.get('pack');
  const [packName, setPackName] = useState('');
  const [opts, setOpts] = useState(null);
  const [models, setModels] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [dateFields, setDateFields] = useState([]);
  const [saving, setSaving] = useState('');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: '', description: '', purpose: 'risk', model_id: '', metric: '', metric_label: '',
    date_field: '', grain: 'auto', lookback_periods: 24, combine: 'or',
    detection: [], materiality: { kind: 'percent', min_pct: 5, min_abs: 0, min_financial: 0, unit_value: 1 },
    persistence: 1, cooldown_hours: 24, base_severity: 'medium',
    notify: { in_app: true, owner: true, roles: ['data_steward', 'admin'], channels: [] },
  });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // AI auto-fill: describe the watch in plain English and let AI populate the form.
  const [aiDesc, setAiDesc] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const runAiFill = async () => {
    if (!form.model_id) return toast.error('Pick a semantic model below first');
    if (!aiDesc.trim()) return toast.error('Describe what you want the Sentinel to watch');
    setAiBusy(true);
    try {
      const d = await api.aiDraftSentinel({ description: aiDesc.trim(), model_id: form.model_id });
      set({
        name: d.name || form.name,
        description: d.description || form.description,
        purpose: d.purpose || form.purpose,
        metric: d.metric || form.metric,
        metric_label: d.metric_label || form.metric_label,
        date_field: d.date_field || form.date_field,
        grain: d.grain || form.grain,
        combine: d.combine || form.combine,
        base_severity: d.base_severity || form.base_severity,
        lookback_periods: d.lookback_periods || form.lookback_periods,
        detection: (d.detection && d.detection.length) ? d.detection : form.detection,
      });
      if (!d.metric_matched) {
        toast.warning('AI filled the details — please choose the metric to watch (it couldn\'t match one automatically).');
      } else {
        toast.success('AI filled in the details — review and adjust before saving.');
      }
    } catch (e) {
      toast.error(e.message || 'AI could not draft this Sentinel');
    } finally {
      setAiBusy(false);
    }
  };

  const loadMeasures = useCallback(async (modelId) => {
    if (!modelId) { setMeasures([]); setDateFields([]); return; }
    try {
      const o = await api.getSentinelOptions(modelId);
      setMeasures(o.measures || []);
      setDateFields(o.date_fields || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [o, ms] = await Promise.all([api.getSentinelOptions(), api.getSemanticModels()]);
        setOpts(o); setModels(ms || []);
        if (isEdit) {
          const res = await api.getSentinel(sentinelId);
          const s = res.sentinel;
          setForm({
            name: s.name, description: s.description || '', purpose: s.purpose, model_id: s.model_id,
            metric: s.metric, metric_label: s.metric_label || '', series_field: s.series_field || '',
            date_field: s.date_field || '', grain: s.grain, lookback_periods: s.lookback_periods,
            combine: s.combine, detection: s.detection || [], materiality: s.materiality,
            persistence: s.persistence, cooldown_hours: s.cooldown_hours, base_severity: s.base_severity,
            notify: s.notify,
          });
          await loadMeasures(s.model_id);
        } else if (packId) {
          const pr = await api.getSentinelPacks();
          const p = (pr.packs || []).find((x) => x.pack_id === packId);
          if (p) {
            const c = p.config || {};
            setForm((f) => ({
              ...f,
              purpose: c.purpose || f.purpose, grain: c.grain || f.grain,
              lookback_periods: c.lookback_periods || f.lookback_periods,
              combine: c.combine || f.combine, detection: c.detection || [],
              materiality: c.materiality || f.materiality, persistence: c.persistence || f.persistence,
              cooldown_hours: c.cooldown_hours || f.cooldown_hours,
              base_severity: c.base_severity || f.base_severity, notify: c.notify || f.notify,
            }));
            setPackName(p.name);
          }
        }
      } catch (e) {
        toast.error(e.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, sentinelId, packId, loadMeasures]);

  const onModelChange = (modelId) => {
    set({ model_id: modelId, metric: '', metric_label: '', date_field: '' });
    loadMeasures(modelId);
  };
  const onMetricChange = (name) => {
    const m = measures.find((x) => x.name === name);
    set({ metric: name, metric_label: m ? (m.label || name) : name });
  };
  const addDetection = (type) => {
    const meta = (opts.detection_types || []).find((d) => d.type === type);
    if (!meta) return;
    set({ detection: [...form.detection, { type, params: { ...meta.params } }] });
  };
  const updateDetection = (i, det) => set({ detection: form.detection.map((d, idx) => (idx === i ? det : d)) });
  const removeDetection = (i) => set({ detection: form.detection.filter((_, idx) => idx !== i) });

  const save = async (activate) => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.model_id) return toast.error('Pick a semantic model');
    if (!form.metric) return toast.error('Pick a metric to watch');
    if (!form.detection.length) return toast.error('Add at least one detection method');
    setSaving(activate ? 'activate' : 'draft');
    try {
      let saved;
      if (isEdit) { saved = await api.editSentinel(sentinelId, form); }
      else { saved = await api.createSentinel(form); }
      const id = saved.sentinel_id || sentinelId;
      if (activate && saved.status !== 'active') { await api.transitionSentinel(id, 'active'); }
      toast.success(isEdit ? 'Sentinel saved' : (activate ? 'Sentinel created & activated' : 'Sentinel saved as draft'));
      navigate(`/admin/sentinels/${id}`);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving('');
    }
  };

  if (loading || !opts) {
    return <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  const availableTypes = (opts.detection_types || []).filter((d) => !form.detection.some((x) => x.type === d.type));
  const mat = form.materiality || {};

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="sentinel-builder-page">
      <button className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        onClick={() => navigate('/admin/sentinels')} data-testid="builder-back">
        <ArrowLeft className="w-4 h-4" /> All Sentinels
      </button>
      <div className="flex items-start justify-between gap-3">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Radar className="w-6 h-6 text-primary" /> {isEdit ? 'Edit Sentinel' : 'New Sentinel'}
        </h1>
        <ProcessHelp
          testId="sentinel-builder-help"
          title="Building a Sentinel"
          section="sentinels"
          intro="Define what to watch, how to detect a change, and when that change is material enough to raise an incident."
          steps={[
            'Identity — name the Sentinel and set its purpose.',
            'Metric & time — pick the semantic model and certified metric, plus an optional time field and grain.',
            'Detection — add one or more detection methods and tune their thresholds; choose AND / OR to combine them.',
            'Materiality & alerting — set the materiality bar, persistence, cooldown, severity and who gets notified.',
            'Save as draft to keep tuning, or Save & Activate to start monitoring. Sentinels only observe and alert — never auto-execute.',
          ]}
        />
      </div>
      {packName ? (
        <div className="rounded-lg border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-xs text-violet-600 dark:text-violet-300 flex items-center gap-2" data-testid="builder-from-pack">
          <Package className="w-3.5 h-3.5" /> Starting from pack <span className="font-medium">{packName}</span> — just pick a model &amp; metric to stamp it on.
        </div>
      ) : null}

      {/* AI auto-fill */}
      {!isEdit && (
        <section className="rounded-xl border border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="sentinel-ai-fill">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <div className="text-sm font-medium">Let AI fill in the details</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <Label className="text-xs">Data source (semantic model)</Label>
              <Select value={form.model_id} onValueChange={onModelChange}>
                <SelectTrigger data-testid="ai-fill-model"><SelectValue placeholder="Choose a model" /></SelectTrigger>
                <SelectContent>{models.map((m) => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Describe what to watch</Label>
              <Textarea rows={2} value={aiDesc} onChange={(e) => setAiDesc(e.target.value)}
                placeholder="Watch monthly loss ratio and alert me when it jumps more than 15% versus the prior month"
                data-testid="ai-fill-input" />
            </div>
          </div>
          <div className="rounded-lg bg-muted/40 border border-border/40 p-2.5 text-[11px] text-muted-foreground leading-relaxed" data-testid="ai-fill-example">
            <span className="font-medium text-foreground">Tip — write it like this so AI fills the page accurately:</span> name the <span className="text-foreground">metric</span>,
            the <span className="text-foreground">time grain</span> (monthly / quarterly), the <span className="text-foreground">condition</span> (spikes, drops, crosses a number, trends for N periods),
            and <span className="text-foreground">how sensitive</span> it should be.
            <br />
            <span className="italic">Example: “Alert when quarterly combined ratio goes above 100 for two quarters in a row, and treat it as high severity.”</span>
          </div>
          <div className="flex justify-end">
            <Button onClick={runAiFill} disabled={aiBusy || !form.model_id || !aiDesc.trim()} data-testid="ai-fill-btn" className="gap-2">
              {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {aiBusy ? 'Drafting…' : 'Fill details with AI'}
            </Button>
          </div>
        </section>
      )}

      {/* Identity */}
      <section className="rounded-xl border border-border/60 bg-card/60 p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Identity &amp; purpose</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs">Name</Label>
            <Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Loss ratio surge watch" data-testid="builder-name" />
          </div>
          <div>
            <Label className="text-xs">Purpose</Label>
            <Select value={form.purpose} onValueChange={(v) => set({ purpose: v })}>
              <SelectTrigger data-testid="builder-purpose"><SelectValue /></SelectTrigger>
              <SelectContent>{(opts.purposes || []).map((p) => <SelectItem key={p} value={p}>{labelize(p)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs">Description</Label>
          <Textarea rows={2} value={form.description} onChange={(e) => set({ description: e.target.value })}
            placeholder="What condition does this Sentinel watch for, and why does it matter?" data-testid="builder-description" />
        </div>
      </section>

      {/* Metric & scope */}
      <section className="rounded-xl border border-border/60 bg-card/60 p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Metric &amp; time</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Semantic model</Label>
            <Select value={form.model_id} onValueChange={onModelChange}>
              <SelectTrigger data-testid="builder-model"><SelectValue placeholder="Choose a model" /></SelectTrigger>
              <SelectContent>{models.map((m) => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Metric (certified measure)</Label>
            <Select value={form.metric} onValueChange={onMetricChange} disabled={!form.model_id}>
              <SelectTrigger data-testid="builder-metric"><SelectValue placeholder={form.model_id ? 'Choose a metric' : 'Pick a model first'} /></SelectTrigger>
              <SelectContent>
                {measures.map((m) => <SelectItem key={m.name} value={m.name}>{m.label || m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Time field (optional)</Label>
            <Select value={form.date_field || 'auto'} onValueChange={(v) => set({ date_field: v === 'auto' ? '' : v })} disabled={!form.model_id}>
              <SelectTrigger data-testid="builder-datefield"><SelectValue placeholder="Auto-detect" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-detect</SelectItem>
                {dateFields.map((d) => <SelectItem key={d.name} value={d.name}>{d.label || d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Grain</Label>
              <Select value={form.grain} onValueChange={(v) => set({ grain: v })}>
                <SelectTrigger data-testid="builder-grain"><SelectValue /></SelectTrigger>
                <SelectContent>{(opts.grains || []).map((g) => <SelectItem key={g} value={g}>{labelize(g)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Lookback</Label>
              <Input type="number" value={form.lookback_periods} onChange={(e) => set({ lookback_periods: Number(e.target.value) })} data-testid="builder-lookback" />
            </div>
          </div>
        </div>
      </section>

      {/* Detection */}
      <section className="rounded-xl border border-border/60 bg-card/60 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Detection methods</div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Combine</span>
            <Select value={form.combine} onValueChange={(v) => set({ combine: v })}>
              <SelectTrigger className="h-8 w-24" data-testid="builder-combine"><SelectValue /></SelectTrigger>
              <SelectContent>{(opts.combine || []).map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {form.detection.map((d, i) => (
          <DetectionEditor key={`${d.type}_${i}`} det={d}
            meta={(opts.detection_types || []).find((m) => m.type === d.type)}
            onChange={(nd) => updateDetection(i, nd)} onRemove={() => removeDetection(i)} />
        ))}
        {availableTypes.length ? (
          <div className="flex items-center gap-2 flex-wrap pt-1">
            {availableTypes.map((d) => (
              <Button key={d.type} size="sm" variant="outline" onClick={() => addDetection(d.type)} data-testid={`add-detection-${d.type}`}>
                <Plus className="w-3.5 h-3.5 mr-1" /> {labelize(d.type)}
              </Button>
            ))}
          </div>
        ) : null}
        {form.detection.length === 0 ? <p className="text-xs text-muted-foreground">Add one or more detection methods above.</p> : null}
      </section>

      {/* Materiality + alerting */}
      <section className="rounded-xl border border-border/60 bg-card/60 p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Materiality &amp; alerting</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Materiality bar</Label>
            <Select value={mat.kind} onValueChange={(v) => set({ materiality: { ...mat, kind: v } })}>
              <SelectTrigger data-testid="builder-materiality-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{(opts.materiality_kinds || []).map((k) => <SelectItem key={k} value={k}>{labelize(k)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {mat.kind === 'percent' ? (
            <div><Label className="text-xs">Min % change</Label>
              <Input type="number" value={mat.min_pct} onChange={(e) => set({ materiality: { ...mat, min_pct: Number(e.target.value) } })} data-testid="builder-min-pct" /></div>
          ) : mat.kind === 'absolute' ? (
            <div><Label className="text-xs">Min absolute change</Label>
              <Input type="number" value={mat.min_abs} onChange={(e) => set({ materiality: { ...mat, min_abs: Number(e.target.value) } })} data-testid="builder-min-abs" /></div>
          ) : (
            <>
              <div><Label className="text-xs">Min financial impact</Label>
                <Input type="number" value={mat.min_financial} onChange={(e) => set({ materiality: { ...mat, min_financial: Number(e.target.value) } })} data-testid="builder-min-financial" /></div>
              <div><Label className="text-xs">Value per unit</Label>
                <Input type="number" value={mat.unit_value} onChange={(e) => set({ materiality: { ...mat, unit_value: Number(e.target.value) } })} data-testid="builder-unit-value" /></div>
            </>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Base severity</Label>
            <Select value={form.base_severity} onValueChange={(v) => set({ base_severity: v })}>
              <SelectTrigger data-testid="builder-severity"><SelectValue /></SelectTrigger>
              <SelectContent>{(opts.severities || []).map((s) => <SelectItem key={s} value={s}>{labelize(s)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Persistence (evals)</Label>
            <Input type="number" value={form.persistence} onChange={(e) => set({ persistence: Number(e.target.value) })} data-testid="builder-persistence" /></div>
          <div><Label className="text-xs">Cooldown (hours)</Label>
            <Input type="number" value={form.cooldown_hours} onChange={(e) => set({ cooldown_hours: Number(e.target.value) })} data-testid="builder-cooldown" /></div>
          <div className="flex items-center gap-2 pt-6">
            <Switch checked={form.notify.owner} onCheckedChange={(v) => set({ notify: { ...form.notify, owner: v } })} data-testid="builder-notify-owner" />
            <Label className="text-xs">Notify owner</Label>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Notifications go in-app to the model owner + stewards/admins. Material actions are never auto-executed —
          Sentinels observe, alert and recommend only.
        </p>
      </section>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/admin/sentinels')}>Cancel</Button>
        <Button variant="outline" disabled={!!saving} onClick={() => save(false)} data-testid="builder-save-draft">
          {saving === 'draft' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save {isEdit ? '' : 'as draft'}</>}
        </Button>
        {!isEdit ? (
          <Button disabled={!!saving} onClick={() => save(true)} data-testid="builder-save-activate">
            {saving === 'activate' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save & Activate'}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
